// components/overlay/IdleSceneKit.jsx
// Shared pieces for the portrait "idle" scenes (Break, Starting Soon) — the
// IG Live cut where a sizzle reel / warm-up cam runs full-screen BEHIND the
// overlay and only a floating pill + CTA + bottom brand band sit on top.
// `accent` / `accentRgb` tint the chrome per scene (Break = gold,
// Starting Soon = sky) so the scenes read as siblings, not clones.
//
// Perf rules apply: static backgrounds, transform-only animations, no
// backdrop-filter, no animated box-shadows.

import JoPLogo from "./JoPLogo";
import FlashDesignsPanel from "./FlashDesignsPanel";
import MarqueeTicker from "./MarqueeTicker";

const CREAM = "var(--jop-cream)";
const D = "var(--jop-display)";
const COL_BG = "linear-gradient(180deg, var(--jop-grad-1) 0%, var(--jop-grad-2) 50%, var(--jop-grad-3) 100%)";

// ── ASCENDING PARTICLES ─────────────────────────────────────────────────────
// Start at the bottom, drift upward, varying sizes. Fairy dust warmth.
// Callers must have the jopAscendDust keyframes in scope (FlashBrandBand
// carries its own copy; re-declaring the same keyframes is harmless).
export function AscendingDust({ count = 14, seed = 1, active = true }) {
  const dots = [];
  for (let i = 0; i < count; i++) {
    const s = ((seed * 7919 + i * 31 + 1) % 100);
    const left = 8 + (s * 0.84) % 84;
    const size = 2 + (i % 5) * 1.2; // varied sizes: 2px to 6.8px
    const delay = (s * 0.11) % 8;
    const dur = 5 + (s % 5); // 5-10s per cycle
    const color = i % 3 === 0 ? CREAM : "var(--jop-gold)";
    const peak = 0.35 + (i % 4) * 0.15;
    dots.push(
      <span key={i} style={{
        position: "absolute",
        left: `${left}%`,
        bottom: `${(s % 20)}%`, // start near bottom with some variation
        width: `${size}px`, height: `${size}px`, borderRadius: "50%",
        background: color,
        boxShadow: `0 0 ${size * 2}px ${color}, 0 0 ${size * 3.5}px ${color}`,
        opacity: 0,
        animation: `jopAscendDust ${dur}s ease-in-out ${delay}s infinite`,
        animationPlayState: active ? "running" : "paused",
        "--jop-dust-peak": peak,
        pointerEvents: "none",
      }} />
    );
  }
  return <div style={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none", zIndex: 0 }}>{dots}</div>;
}

// (The old JoinCtaCard lived here — replaced 2026-07-07 by the flow-voiced
// SummonBanner layer mounted inside each scene's feed region. See
// components/overlay/SummonBanner.jsx + utils/qrFlow.js.)

// ── QUEUE CHIP — live status line under the idle-scene pill ─────────────────
// The one number people deciding whether to wait around actually want.
// Translucent ink so the reel stays visible; renders nothing when there's
// nothing worth saying (closed + no closedText).
export function IdleQueueChip({
  open, queueDepth = 0, waitMin = null,
  accent = "var(--jop-gold)",
  closedText = null,
  fmtWait,
}) {
  let text = null;
  if (open) {
    text = queueDepth > 0
      ? `🥞 ${queueDepth} in line${waitMin != null && fmtWait ? ` · ⏱ ${fmtWait(waitMin)} wait` : ""}`
      : "🥞 First in line — no wait!";
  } else if (closedText) {
    text = closedText;
  }
  if (!text) return null;
  return (
    <div style={{
      background: "rgba(var(--jop-ink-rgb), 0.78)",
      border: `2px solid rgba(var(--jop-cream-rgb), 0.25)`,
      borderRadius: "999px",
      padding: "0.45rem 1.4rem",
      color: accent, fontFamily: D, fontWeight: 800,
      fontSize: "1.5rem", lineHeight: 1.2,
      letterSpacing: "0.02em",
      textShadow: "0 2px 8px rgba(0,0,0,0.5)",
      whiteSpace: "nowrap",
    }}>
      {text}
    </div>
  );
}

// ── CAPTION BAR — slim rotating strip pinned just above the flash band ──────
// Replaces the mid-stack TickerPill + Did You Know / showcase cards (the
// 2026-08-29 declutter): the reel behind the idle scenes is the draw, so the
// only rotating text surface now lives at the very bottom, riding the flash
// band's top edge. Per-shift marquee messages, scene extras (Break's facts),
// and the website URL all share ONE rotation. Deliberately BELOW the IG-safe
// floor (y≈1320): stream comments may cover it — the booth panel shows it in
// full, and clearing the reel's midfield is worth the trade. Bottom offset is
// derived from FlashBrandBand's showUrl=false height so the two never
// overlap; absolute + rigid so rotation reflows nothing.
export function IdleCaptionBar({ messages, accentRgb = "var(--jop-gold-rgb)" }) {
  const items = [
    ...(messages || []),
    { text: "✨ thejoyofpancakes.com ✨", durationSec: 8 },
  ];
  return (
    <div style={{
      position: "absolute", left: "50%", transform: "translateX(-50%)",
      bottom: "calc(24.5vh - 52px + 14px)",
      width: "min(920px, calc(100vw - 80px))",
      height: "140px",
      background: "rgba(var(--jop-ink-rgb), 0.72)",
      border: `2px solid rgba(${accentRgb}, 0.4)`,
      borderRadius: "20px",
      boxSizing: "border-box",
      padding: "0.6rem 1.6rem",
      display: "flex", alignItems: "center", justifyContent: "center",
      textAlign: "center",
      overflow: "hidden",
      fontFamily: D,
      textShadow: "0 2px 8px rgba(0,0,0,0.5)",
      zIndex: 2, pointerEvents: "none",
    }}>
      <MarqueeTicker messages={items} fontSize="1.4rem" color={CREAM} cardCompact />
    </div>
  );
}

// ── FLASH BRAND BAND — bottom 24.5vh of EVERY portrait service scene ────────
// Booth, Break, and Starting Soon all mount this band, so the flash grid sits
// in the exact same spot across a scene cut — zero shift. IG comment-zone
// sacrifice band for stream viewers, but the IRL booth panel shows it in
// full, so it moonlights as the flash menu. The logo RESTS ON the band's top
// edge (absolutely positioned — hiding it never moves the grid; Booth passes
// showLogo=false since its TopBar already carries the logo). URL rides the
// top row; the staggered 8+1 flash board (freestyle "?" included) fills the
// rest. dust=false (Booth) skips the ascending particles — that scene runs
// all event long and stays compositor-quiet. `spotlight` passes through to
// FlashDesignsPanel for gala mode's curated column. showUrl=false (Booth)
// drops the URL row AND shaves exactly its 52px off the band height — the
// flash grid box stays in the same absolute position (zero shift on a scene
// cut) while the scene above gets the height back.
// `marquee` (optional node, e.g. a MarqueeTicker) rides the LEFT side of the
// URL row — only meaningful with showUrl (the row is the slot). As of
// 2026-07-09 every portrait scene runs showUrl=false (the row crowded the
// flash grid); marquee + URL live in the idle stacks' TickerPill instead.
export function FlashBrandBand({
  designs, active,
  accent = "var(--jop-gold)",
  showLogo = true,
  dust = true,
  spotlight = false,
  showUrl = true,
  marquee = null,
}) {
  return (
    <div style={{
      position: "absolute", left: 0, right: 0, bottom: 0,
      height: showUrl ? "24.5vh" : "calc(24.5vh - 52px)", background: COL_BG,
      borderTop: `3px solid ${accent}`,
      boxSizing: "border-box",
      display: "flex", flexDirection: "column",
      padding: "6px 40px 10px",
      zIndex: 2, overflow: "visible",
    }}>
      {dust && <AscendingDust count={8} seed={99} active={active} />}
      {/* Logo straddling the band's top edge, left-anchored like Booth */}
      {showLogo && (
        <div style={{ position: "absolute", left: "36px", top: "-88px", zIndex: 3 }}>
          <JoPLogo variant="wide" height={150} animated />
        </div>
      )}
      {showUrl && (
        <div style={{
          flexShrink: 0,
          display: "flex", alignItems: "center", justifyContent: "flex-end",
          gap: "1.4rem",
          // Exactly 52px tall (not a min) — showUrl=false subtracts this
          // same amount from the band height to keep the grid box put.
          height: "52px",
          position: "relative", zIndex: 1,
        }}>
          {marquee && (
            <div style={{
              flex: 1, minWidth: 0, overflow: "hidden",
              display: "flex", alignItems: "center",
            }}>
              {marquee}
            </div>
          )}
          <div style={{
            flexShrink: 0,
            color: accent, fontFamily: D,
            fontSize: "1.9rem", fontWeight: 900, letterSpacing: "0.05em",
            textShadow: "0 2px 10px rgba(0,0,0,0.5)",
            whiteSpace: "nowrap",
          }}>
            ✨ thejoyofpancakes.com
          </div>
        </div>
      )}
      <div style={{
        flex: 1, minHeight: 0,
        display: "flex", flexDirection: "column",
        position: "relative", zIndex: 1,
        marginTop: "2px",
      }}>
        {designs && designs.length > 0 ? (
          <FlashDesignsPanel designs={designs} portrait spotlight={spotlight} labelMode="none" stagger />
        ) : null}
      </div>
      <style>{`
        @keyframes jopAscendDust {
          0%   { opacity: 0; transform: translateY(0) scale(0.6); }
          15%  { opacity: var(--jop-dust-peak, 0.5); }
          50%  { opacity: calc(var(--jop-dust-peak, 0.5) * 0.7); transform: translateY(-180px) scale(1); }
          85%  { opacity: calc(var(--jop-dust-peak, 0.5) * 0.2); }
          100% { opacity: 0; transform: translateY(-350px) scale(0.4); }
        }
      `}</style>
    </div>
  );
}
